<template>
  <div class="invoice-data-table-contain">
    <vue-good-table
      class="invoice-data-table"
      :class="{ 'invoice-data-table-action': type === TYPE.REPORTED }"
      :rows="invoiceList"
      :columns="columns"
      :sort-options="{ enabled: false }"
      :isLoading="isLoading"
      @on-row-click="onRowClick"
    >
      <template slot="table-column" slot-scope="props">
        <b-form-checkbox
          v-if="props.column.field === `checkbox`"
          :checked="selectAllState === true"
          :indeterminate="selectAllState === null"
          @change="onSelectAll"
        />
      </template>
      <template slot="table-row" slot-scope="props">
        <b-form-checkbox-group :checked="selectedIds" v-if="props.column.field === `checkbox`">
          <b-form-checkbox
            :id="props.row.id.toString()"
            :value="props.row.id"
            @change="() => onSelectionChanged(props.row)"
          />
        </b-form-checkbox-group>
        <span v-else-if="props.column.field === `invoice_serial`">
          <span class="field-label">{{ $t('reconciliation.serial') }}:</span>
          <span class="field-value">{{ props.row.invoice_serial }}</span>
        </span>
        <span v-else-if="props.column.field === `invoice_number`">
          <span class="field-label">{{ $t('reconciliation.number') }}:</span>
          <span class="field-value">{{ props.row.invoice_number }}</span>
        </span>
        <span v-else-if="props.column.field === `issued_date`">
          <span class="field-label">{{ $t('reconciliation.time') }}:</span>
          <span class="field-value">{{ props.row.issued_date }}</span>
        </span>
        <span v-else-if="props.column.field === `legalName`">
          <span class="field-label">{{ $t('reconciliation.legalName') }}:</span>
          <span class="field-value">{{ props.row.kind_of ? props.row.buyer_name : props.row.seller_name }}</span>
        </span>
        <span v-else-if="props.column.field === `total_amount_with_tax`">
          <span class="field-label">{{ $t('invoice.grandTotal') }}:</span>
          <span class="field-value">{{ NumericUtils.formatAmount(props.row.total_amount_with_tax) }}</span>
        </span>
        <span v-else-if="props.column.field === `invoice_type`">
          <span class="field-label">{{ $t('reconciliation.status') }}:</span>
          <span class="field-value">{{ $t(`invoice.invoiceTypes.${props.row.invoice_type}`) }}</span>
        </span>
        <span v-else-if="props.column.field === `tags`" class="tag-field">
          <span class="field-label">Tag:</span>
          <span class="field-value">
            <label class="badge">
              <span v-if="props.row.releasing_status" :class="`bg-${props.row.releasing_status}`">{{ $t(`invoice.releasingStatus.${props.row.releasing_status}`) }}</span>
              <span v-else-if="props.row.paid_date" class="bg-tint-4">{{$t('invoice.paid')}}: {{props.row.paid_date | formatDate}}</span>
              <span v-else class="bg-error">{{$t('invoice.unpaid')}}</span>
            </label>
            <label v-if="props.row.tags && props.row.tags.length > 0" v-for="tag in props.row.tags.slice(0,3)" class="badge">
              <span :style="{backgroundColor: findTagInfoById(tag.id)}">
               {{tag.name | tagName}}
              </span>
            </label>
          </span>
        </span>
        <span v-else-if="props.column.field === `status_name`">
          <span class="field-label">{{ $t('reconciliation.status') }}:</span>
          <span class="field-value">
            <label class="badge mb-0" v-html="generateStatusTag(props.formattedRow[props.column.field])"></label>
          </span>
        </span>
        <div v-else-if="props.column.field === 'actions'">
          <span class="field-label">{{ $t('app.actions') }}:</span>
          <span class="field-value">
            <div class="d-flex justify-content-between">
              <b-button @click="onClickReportedDetail(props.row, 'downloadPDF')" variant="outline-light">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 18V15.5M7 15.5V14C7 13.529 7 13.293 7.154 13.146C7.308 13 7.555 13 8.05 13H8.75C9.475 13 10.063 13.56 10.063 14.25C10.063 14.94 9.475 15.5 8.75 15.5H7ZM21 13H19.688C18.863 13 18.45 13 18.194 13.244C17.938 13.488 17.938 13.881 17.938 14.667V15.5M17.938 15.5V18M17.938 15.5H20.125M15.75 15.5C15.75 16.88 14.575 18 13.125 18C12.798 18 12.635 18 12.512 17.933C12.221 17.773 12.25 17.448 12.25 17.167V13.833C12.25 13.552 12.22 13.227 12.512 13.067C12.634 13 12.798 13 13.125 13C14.575 13 15.75 14.12 15.75 15.5Z" stroke="#002856" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M15 22H10.727C7.467 22 5.835 22 4.703 21.202C4.38085 20.9761 4.0929 20.705 3.848 20.397C3 19.331 3 17.797 3 14.727V12.182C3 9.219 3 7.737 3.469 6.554C4.223 4.651 5.817 3.151 7.839 2.441C9.095 2 10.668 2 13.818 2C15.616 2 16.516 2 17.234 2.252C18.389 2.658 19.3 3.515 19.731 4.602C20 5.278 20 6.125 20 7.818V10" stroke="#002856" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3 12C3 11.116 3.35115 10.2683 3.97621 9.64321C4.60127 9.01815 5.44903 8.667 6.333 8.667C6.999 8.667 7.784 8.783 8.431 8.61C8.71371 8.53392 8.97145 8.38485 9.17838 8.17774C9.38531 7.97064 9.53416 7.71277 9.61 7.43C9.783 6.783 9.667 5.998 9.667 5.332C9.66726 4.44821 10.0185 3.6007 10.6436 2.97586C11.2686 2.35102 12.1162 2 13 2" stroke="#002856" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </b-button>
              <b-button @click="onClickReportedDetail(props.row, 'downloadXML')" variant="outline-light">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 13L8.647 15.5M8.647 15.5L10.294 18M8.647 15.5L10.294 13M8.647 15.5L7 18M21 18H20.177C19.4 18 19.012 18 18.771 17.756C18.529 17.512 18.529 17.119 18.529 16.333V13M12.353 18L12.695 13.835C12.724 13.481 12.738 13.305 12.845 13.272C12.952 13.239 13.061 13.377 13.28 13.654L14.153 14.758C14.272 14.908 14.331 14.983 14.41 14.983C14.489 14.983 14.549 14.908 14.667 14.758L15.541 13.653C15.759 13.377 15.869 13.238 15.975 13.271C16.082 13.304 16.097 13.481 16.126 13.834L16.471 18" stroke="#002856" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M15 22H10.727C7.467 22 5.835 22 4.703 21.202C4.38085 20.9761 4.0929 20.705 3.848 20.397C3 19.331 3 17.797 3 14.727V12.182C3 9.219 3 7.737 3.469 6.554C4.223 4.651 5.817 3.151 7.839 2.441C9.095 2 10.668 2 13.818 2C15.616 2 16.516 2 17.234 2.252C18.389 2.658 19.3 3.515 19.731 4.602C20 5.278 20 6.125 20 7.818V10" stroke="#002856" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3 12C3 11.116 3.35115 10.2683 3.97621 9.64321C4.60127 9.01815 5.44903 8.667 6.333 8.667C6.999 8.667 7.784 8.783 8.431 8.61C8.71371 8.53392 8.97145 8.38485 9.17838 8.17774C9.38531 7.97064 9.53416 7.71277 9.61 7.43C9.783 6.783 9.667 5.998 9.667 5.332C9.66726 4.44821 10.0185 3.6007 10.6436 2.97586C11.2686 2.35102 12.1162 2 13 2" stroke="#002856" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </b-button>
            </div>
          </span>
        </div>
        <div v-else-if="props.column.field === 'data'">
          <span class="field-label">{{ $t('app.data') }}:</span>
          <span class="field-value">
            <b-button @click="onClickReportedDetail(props.row, 'view')" variant="outline-light">
              <i class="fad fa-analytics" style="color: #005DCA"></i>
            </b-button>
          </span>
        </div>
        <span v-else-if="props.column.field === 'request_date'">
          <span class="field-label">Ngày báo cáo:</span>
          <span class="field-value">{{ props.row.request_date ? DateTimeUtils.parseDate(new Date(props.row.request_date)).format('YYYY-MM-DD') : '' }}</span>
        </span>
        <span v-else-if="props.column.field === 'ma_thong_diep'">
          <span class="field-label">Mã Thông điệp:</span>
          <span class="field-value">{{ props.row.ma_thong_diep }}</span>
        </span>
        <span v-else-if="props.column.field === 'list_invoice_no'">
          <span class="field-label">Hóa đơn:</span>
          <span class="field-value">{{ props.row.list_invoice_no ? props.row.list_invoice_no.replace(/;/g, ' ') : '' }}</span>
        </span>
      </template>
    </vue-good-table>
    <div v-if="type === TYPE.UNREPORTED" class="my-3 d-flex flex-lg-row flex-col justify-content-lg-between justify-content-center align-items-center">
      <div v-if="!isLoading" class="d-flex justify-content-start align-items-center">
        <b-form-select style="max-width: 120px" v-model="pageInfo.current_limit" :options="options" @change="onChangeTotal"></b-form-select>
        <span class="ml-3" style="white-space: nowrap;">
          {{ $t('reconciliation.From') }} {{ startRow }} {{ $t('reconciliation.to') }} {{ endRow }} - {{ $t('app.total') }}: {{ pageInfo.total }}
        </span>
      </div>
      <b-pagination
        v-if="!isLoading"
        v-model="pageInfo.current_page"
        :page="pageInfo.current_page"
        :total-rows="pageInfo.total"
        :per-page="pageInfo.current_limit"
        first-number
        last-number
        @page-click="onPageClick"
      ></b-pagination>
    </div>
    <div v-if="selectedRows.length && type === TYPE.UNREPORTED" class="mt-3">
      <h5 style="color: #005DCA">Các hóa đơn đã chọn {{`(${selectedRows.length})`}}</h5>
    </div>
    <vue-good-table
      class="invoice-data-table"
      :sort-options="{ enabled: false }"
      v-if="selectedRows.length && type === TYPE.UNREPORTED"
      :rows="selectedRows"
      :columns="selectedColumns"
    >
      <template slot="table-row" slot-scope="props">
        <div v-if="props.column.field === 'action_delete'">
          <b-button variant="link" @click="() => onSelectionChanged(props.row)" class="p-2">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g opacity="0.7">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.99908 9.41474L11.5344 12.9501C11.722 13.1377 11.9764 13.2431 12.2418 13.2431C12.5071 13.2431 12.7615 13.1377 12.9491 12.9501C13.1367 12.7625 13.2421 12.508 13.2421 12.2427C13.2421 11.9774 13.1367 11.723 12.9491 11.5354L9.41242 8.00007L12.9484 4.46474C13.0413 4.37185 13.1149 4.26159 13.1651 4.14024C13.2154 4.01889 13.2412 3.88884 13.2412 3.7575C13.2411 3.62617 13.2152 3.49613 13.165 3.37481C13.1147 3.25348 13.041 3.14325 12.9481 3.05041C12.8552 2.95756 12.7449 2.88392 12.6236 2.83369C12.5022 2.78346 12.3722 2.75762 12.2408 2.75766C12.1095 2.75769 11.9795 2.78358 11.8582 2.83387C11.7368 2.88416 11.6266 2.95785 11.5338 3.05074L7.99908 6.58607L4.46375 3.05074C4.37155 2.95519 4.26124 2.87895 4.13926 2.82649C4.01728 2.77402 3.88608 2.74638 3.7533 2.74516C3.62052 2.74394 3.48883 2.76918 3.36591 2.81941C3.24299 2.86963 3.1313 2.94383 3.03737 3.03768C2.94343 3.13153 2.86913 3.24314 2.81879 3.36601C2.76845 3.48889 2.74308 3.62055 2.74417 3.75333C2.74527 3.88611 2.77279 4.01734 2.82514 4.13937C2.87749 4.2614 2.95362 4.37178 3.04908 4.46407L6.58575 8.00007L3.04975 11.5361C2.95429 11.6284 2.87816 11.7387 2.82581 11.8608C2.77346 11.9828 2.74593 12.114 2.74484 12.2468C2.74375 12.3796 2.76911 12.5113 2.81945 12.6341C2.86979 12.757 2.9441 12.8686 3.03803 12.9625C3.13197 13.0563 3.24366 13.1305 3.36658 13.1807C3.4895 13.231 3.62119 13.2562 3.75396 13.255C3.88674 13.2538 4.01795 13.2261 4.13993 13.1737C4.26191 13.1212 4.37222 13.045 4.46442 12.9494L7.99908 9.41474Z" fill="#005DCA"/>
              </g>
            </svg>
          </b-button>
        </div>
        <span v-else-if="props.column.field === `legalName`" :data-label="$t('reconciliation.legalName')">
          {{ props.row.kind_of ? props.row.buyer_name : props.row.seller_name }}
        </span>
        <span v-else-if="props.column.field === `tags`" :data-label="'Tag'" class="tag-field">
          <label class="badge">
            <span v-if="props.row.releasing_status" :class="`bg-${props.row.releasing_status}`">{{ $t(`invoice.releasingStatus.${props.row.releasing_status}`) }}</span>
            <span v-else-if="props.row.paid_date" class="bg-tint-4">{{$t('invoice.paid')}}: {{props.row.paid_date | formatDate}}</span>
            <span v-else class="bg-error">{{$t('invoice.unpaid')}}</span>
          </label>
          <label v-if="props.row.tags && props.row.tags.length > 0" v-for="tag in props.row.tags.slice(0,3)" class="badge">
            <span :style="{backgroundColor: findTagInfoById(tag.id)}">
             {{tag.name | tagName}}
            </span>
          </label>
        </span>
      </template>
    </vue-good-table>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import 'vue-good-table/dist/vue-good-table.css';
import { VueGoodTable } from 'vue-good-table';
import NumericUtils from '../../utils/numeric';
import ToastUtils from '../../utils/toast';
import { DOWNLOAD_URL } from '../../app-constant';
import DateTimeUtils from '../../utils/datetime';

const TYPE = {
  UNREPORTED: 'UNREPORTED',
  REPORTED: 'REPORTED',
};

function hasAncestorWithoutClass(element, className) {
  if (element.classList && element.classList.contains(className)) {
    return false; // Ancestor has the class
  }
  let ancestor = element.parentNode;
  while (ancestor !== null && ancestor !== document) {
    if (ancestor.classList && ancestor.classList.contains(className)) {
      return false; // Ancestor has the class
    }
    ancestor = ancestor.parentNode;
  }
  return true; // No ancestor has the class
}


export default {
  name: 'ErrorReportedTable',
  components: {
    VueGoodTable,
  },
  props: {
    selectedList: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    invoiceList: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: TYPE.UNREPORTED,
      validator: value => [TYPE.UNREPORTED, TYPE.REPORTED].includes(value),
    },
    pageInfo: {
      type: Object,
      default: {
        total: 0,
        current_page: 1,
        current_limit: 5,
      },
    },
  },
  data() {
    return {
      selected: new Map(),
      selectAllState: false,
      options: [
        { value: 5, text: `${this.$t('app.Show')}: 5` },
        { value: 10, text: `${this.$t('app.Show')}: 10` },
        { value: 25, text: `${this.$t('app.Show')}: 25` },
        { value: 50, text: `${this.$t('app.Show')}: 50` },
        { value: 100, text: `${this.$t('app.Show')}: 100` },
      ],
    };
  },
  computed: {
    ...mapState([
      'invoiceGlobalSearchParams',
      'invoiceListEnum',
      'tags',
    ]),
    TYPE() {
      return TYPE;
    },
    selectedIds() {
      return this.selected.size ? [...this.selected.keys()] : [];
    },
    selectedRows() {
      return this.selected.size ? [...this.selected.values()] : [];
    },
    selectedColumns() {
      return [
        {
          field: 'action_delete',
          width: '20px',
        },
        {
          field: 'invoice_serial',
          width: '68px',
        },
        {
          field: 'invoice_number',
        },
        {
          field: 'issued_date',
          width: '88px',
        },
        {
          field: 'legalName',
        },
        {
          field: 'total_amount_with_tax',
          thClass: 'text-right',
          tdClass: 'text-right',
          formatFn: value => NumericUtils.formatAmount(value),
        },
        {
          field: 'invoice_type',
          thClass: 'text-center',
          tdClass: 'text-center',
          formatFn: value => (this.$t(`invoice.invoiceTypes.${value}`)),
        },
        {
          field: 'tags',
          thClass: 'text-center',
          tdClass: 'text-right',
        },
      ];
    },
    columns() {
      if (this.type === TYPE.UNREPORTED) {
        return [
          {
            thClass: 'vgt-checkbox',
            tdClass: 'vgt-checkbox',
            field: 'checkbox',
            width: '20px',
          },
          {
            label: this.$t('reconciliation.serial'),
            field: 'invoice_serial',
            width: '68px',
          },
          {
            label: this.$t('reconciliation.number'),
            field: 'invoice_number',
          },
          {
            label: this.$t('reconciliation.time'),
            field: 'issued_date',
            width: '88px',
          },
          {
            label: this.$t('reconciliation.legalName'),
            field: 'legalName',
          },
          {
            label: this.$t('invoice.grandTotal'),
            field: 'total_amount_with_tax',
            thClass: 'text-right',
            tdClass: 'text-right',
            formatFn: value => NumericUtils.formatAmount(value),
          },
          {
            label: this.$t('reconciliation.status'),
            field: 'invoice_type',
            thClass: 'text-center',
            tdClass: 'text-center',
            formatFn: value => (this.$t(`invoice.invoiceTypes.${value}`)),
          },
          {
            label: 'Tag',
            field: 'tags',
            thClass: 'text-center',
            tdClass: 'text-right',
          },
        ];
      }
      return [
        {
          label: 'Ngày báo cáo',
          field: 'request_date',
          thClass: 'px-3',
          tdClass: 'px-3',
          formatFn: value => (value ? DateTimeUtils.parseDate(new Date(value)).format('YYYY-MM-DD') : ''),
        },
        {
          label: 'Dữ liệu',
          field: 'data',
        },
        {
          label: 'Mã Thông điệp',
          field: 'ma_thong_diep',
        },
        {
          label: 'Hóa đơn',
          field: 'list_invoice_no',
          formatFn: value => (value ? value.replace(/;/g, ' ') : ''),
        },
        {
          label: 'Trạng thái',
          field: 'status_name',
        },
        {
          label: 'Download',
          field: 'actions',
          width: '100px',
        },
      ];
    },
    startRow() {
      return ((this.pageInfo.current_page - 1) * this.pageInfo.current_limit) + 1;
    },
    endRow() {
      const end = this.pageInfo.current_page * this.pageInfo.current_limit;
      return end > this.pageInfo.total ? this.pageInfo.total : end;
    },
  },
  mounted() {
    this.initialize();
    this.addOrderClassesToTd();
  },
  updated() {
    this.addOrderClassesToTd();
  },
  methods: {
    ...mapActions([
      'downloadXmlBcss',
    ]),
    generateStatusTag(status) {
      if (status !== 'Có sai sót dữ liệu') {
        return '<span class="bg-success px-4 py-2" style="border-radius: 32px; font-size: 14px">Thành công</span>';
      }
      return '<span class="bg-danger px-4 py-2" style="border-radius: 32px; font-size: 14px">Báo cáo lỗi</span>';
    },
    clear() {
      this.selected = new Map();
      this.selectAllState = false;
    },
    initialize() {
      this.$nextTick(() => {
        if (this.selectedList.length) {
          this.selectAllState = null;
          this.selected = new Map(this.selectedList.map(invoice => [invoice.id, invoice]));
        }
      });
    },
    addOrderClassesToTd() {
      this.$nextTick(() => {
        // Target các td theo thứ tự xuất hiện trong HTML
        const tds = this.$el.querySelectorAll('tbody tr td');
        
        tds.forEach((td, index) => {
          // Bỏ qua checkbox (index 0) và actions (index cuối)
          if (index === 0 || index === tds.length - 1) return;
          
          // Thêm class theo thứ tự field
          const fieldOrder = index - 1; // Trừ đi checkbox
          const fieldNames = [
            'invoice_serial',      // Ký hiệu
            'invoice_number',      // Số  
            'issued_date',         // Thời gian
            'legalName',           // Tên đơn vị
            'total_amount_with_tax', // Tổng tiền
            'invoice_type',        // Trạng thái
            'error_reported',      // Kiểm tra lỗi
            'tags'                 // Tag
          ];
          
          if (fieldOrder < fieldNames.length) {
            td.classList.add(`order-field-${fieldNames[fieldOrder]}`);
          }
        });
      });
    },
    updateSelectAllState() {
      const isNoData = this.invoiceList.length === 0;
      const allSelected = isNoData ? false : this.invoiceList.every(invoice => this.selected.has(invoice.id));
      const noneSelected = isNoData ? true : this.invoiceList.every(invoice => !this.selected.has(invoice.id));

      if (allSelected) {
        this.selectAllState = true; // Checked
      } else if (noneSelected) {
        this.selectAllState = false; // Unchecked
      } else {
        this.selectAllState = null; // Indeterminate
      }
    },
    findTagInfoById(id) {
      const tagInfo = this.tags.find(t => t.id === id);
      return tagInfo ? tagInfo.hex_color || '#CCC' : '#CCC';
    },
    onChangeTotal(value) {
      this.$emit('on-change-total', value);
    },
    onPageClick(_, page) {
      this.$emit('on-page-click', page);
    },
    onSelectionChanged(invoice) {
      if (this.selected.has(invoice.id)) {
        this.selected.delete(invoice.id);
      } else {
        this.selected.set(invoice.id, { ...invoice });
      }
      this.selected = new Map(this.selected);
      this.$emit('on-selected', this.selectedRows);
      this.updateSelectAllState();
    },
    onSelectAll(item) {
      this.invoiceList.forEach((invoice) => {
        const isSelected = this.selected.has(invoice.id);
        if (item && !isSelected) {
          this.selected.set(invoice.id, { ...invoice });
        } else if (!item && isSelected) {
          this.selected.delete(invoice.id);
        }
      });
      this.selected = new Map(this.selected);
      this.$emit('on-selected', this.selectedRows);
      this.updateSelectAllState();
    },
    onClickReportedDetail(item, check) {
      // else if (check === 'viewResData') {}
      if (check === 'view') {
        if (item.request_data && item.request_data.data) {
          this.$modal.show('dialog', {
            title: 'Dữ liệu báo cáo',
            text: item.request_data.data,
          });
        }
      } else if (check === 'downloadXML') {
        this.downloadXmlBcss({
          id: item.id,
          nameFile: `bcss_SHD_${item.list_invoice_no.replace(/;/g, '-').slice(0, -1)}`,
          nameType: 'zip',
        })
          .then(() => {
            ToastUtils.showToast('success', 'Tải báo cáo sai sót thành công');
          })
          .catch((error) => {
            ToastUtils.showToast('error', error.message || error);
          });
      } else if (check === 'downloadPDF') {
        if (item.signed_pdf_url) {
          const urlExport = DOWNLOAD_URL + encodeURIComponent(item.signed_pdf_url);
          window.open(urlExport, '_blank');
          ToastUtils.showToast('success', 'Tải báo cáo sai sót thành công');
        } else {
          ToastUtils.showToast('error', 'Không có báo cáo sai sót nào');
        }
      }
    },
    onRowClick(params) {
      const { event, row } = params;
      if (hasAncestorWithoutClass(event.target, 'vgt-checkbox') && this.type !== TYPE.REPORTED) {
        // if (row.releasing_status && ['draf', 'release_error'].includes(row.releasing_status)) {
        //   this.onIssueInvoice(row);
        // }
        this.$modal.show('invoice-viewer-modal', {
          isPagination: false,
          model: row,
          currentIndex: this.invoiceList.findIndex(invoice => row.id === invoice.id),
        });
      }
    }
  },
  watch: {
    invoiceList() {
      this.updateSelectAllState();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/portal-components/invoice/error-reported-table.vue